import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/LangContext";

const linksMap = (t) => [
  { anchor: "manifesto", label: t.nav.manifesto },
  { anchor: "process", label: t.nav.process },
  { anchor: "services", label: t.nav.services },
  { anchor: "make", label: t.nav.what_we_do },
  { anchor: "clients", label: t.nav.clients },
  { to: "/certifications", label: t.nav.certifications },
  { to: "/start", label: t.nav.configurator },
];

function useHashScroll() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/") return;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (!el) return;
    // small delay to let framer-motion mount
    setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, [hash, pathname]);
}

export function HashScrollBoot() {
  useHashScroll();
  return null;
}

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = linksMap(t);

  function anchorHref(anchor) {
    return onLanding ? `#${anchor}` : `/#${anchor}`;
  }

  function handleAnchor(e, anchor) {
    if (onLanding) return; // let Lenis anchor handler take over
    e.preventDefault();
    setOpen(false);
    navigate(`/#${anchor}`);
  }

  function renderDesktopLink(l) {
    if (l.to) {
      return (
        <Link
          key={l.to}
          to={l.to}
          data-testid={`nav-link-${l.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
          className="text-sm text-white/70 hover:text-white transition-colors font-medium"
        >
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.anchor}
        href={anchorHref(l.anchor)}
        onClick={(e) => handleAnchor(e, l.anchor)}
        data-testid={`nav-link-${l.anchor}`}
        className="text-sm text-white/70 hover:text-white transition-colors font-medium"
      >
        {l.label}
      </a>
    );
  }

  function renderMobileLink(l, i) {
    const commonProps = {
      onClick: (e) => {
        if (l.anchor) handleAnchor(e, l.anchor);
        setOpen(false);
      },
      className: "font-display text-4xl font-bold text-white/90 hover:text-red-400 transition-colors",
    };
    if (l.to) {
      return (
        <motion.div
          key={l.to}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 * i, duration: 0.5 }}
        >
          <Link to={l.to} onClick={() => setOpen(false)} className={commonProps.className}>
            {l.label}
          </Link>
        </motion.div>
      );
    }
    return (
      <motion.a
        key={l.anchor}
        href={anchorHref(l.anchor)}
        {...commonProps}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.06 * i, duration: 0.5 }}
      >
        {l.label}
      </motion.a>
    );
  }

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
          <a href={onLanding ? "#top" : "/"} onClick={(e) => { if (onLanding) { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } }} data-testid="nav-logo" className="flex items-center gap-2 group">
            <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_16px_rgba(239, 68, 68,0.8)] group-hover:scale-125 transition-transform" />
            <span className="font-display font-black tracking-tight text-[17px] uppercase">
              Emergent<span className="text-red-400">.</span>Food
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map(renderDesktopLink)}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-1 text-xs font-mono-ef" data-testid="lang-switcher">
              <button
                data-testid="lang-it"
                onClick={() => setLang("it")}
                className={`px-2 py-1 rounded-full transition-colors ${
                  lang === "it" ? "text-white bg-white/10" : "text-white/40 hover:text-white/80"
                }`}
              >
                IT
              </button>
              <span className="text-white/20">/</span>
              <button
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={`px-2 py-1 rounded-full transition-colors ${
                  lang === "en" ? "text-white bg-white/10" : "text-white/40 hover:text-white/80"
                }`}
              >
                EN
              </button>
            </div>
            <Link to="/start" data-testid="nav-cta" className="btn-pill btn-primary hidden md:inline-flex">
              {t.nav.contact}
            </Link>
            <button
              className="lg:hidden text-white/80 hover:text-white"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#050505] flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between h-16 px-6 border-b border-white/5">
              <span className="font-display font-black uppercase">Emergent.Food</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                className="text-white/80 hover:text-white"
                aria-label="Close menu"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-6">
              {links.map(renderMobileLink)}
            </nav>
            <div className="p-6 border-t border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-mono-ef">
                <button
                  onClick={() => setLang("it")}
                  className={`px-2 py-1 rounded-full ${lang === "it" ? "text-white bg-white/10" : "text-white/40"}`}
                >
                  IT
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-2 py-1 rounded-full ${lang === "en" ? "text-white bg-white/10" : "text-white/40"}`}
                >
                  EN
                </button>
              </div>
              <Link to="/start" onClick={() => setOpen(false)} className="btn-pill btn-primary text-xs">
                {t.nav.contact}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
